import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Fade,
  IconButton,
  Paper,
  Rating,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import apiService from "../../../../../../service/Api";
import { showErrorToast } from "../../../../../../components/common/ShowToast";

interface RateExperienceModalProps {
  streamId: number;
  open: boolean;
  onClose: () => void;
}

const dismissedKey = (streamId: number) => `memorial_rating_prompted_${streamId}`;
const THANK_YOU_DURATION_MS = 5600;

export default function RateExperienceModal({
  streamId,
  open,
  onClose,
}: RateExperienceModalProps) {
  const [value, setValue] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [visible, setVisible] = useState(false);
  const [shrinkBar, setShrinkBar] = useState(false);

  useEffect(() => {
    if (!open) {
      setVisible(false);
      setValue(null);
      setComment("");
      setSubmitted(false);
      setSubmitting(false);
      setShrinkBar(false);
      return;
    }

    if (localStorage.getItem(dismissedKey(streamId))) {
      onClose();
      return;
    }

    setVisible(true);
  }, [open, streamId, onClose]);

  useEffect(() => {
    if (!submitted) return;

    setShrinkBar(false);
    const raf = requestAnimationFrame(() => setShrinkBar(true));
    return () => cancelAnimationFrame(raf);
  }, [submitted]);

  const handleClose = () => {
    localStorage.setItem(dismissedKey(streamId), "1");
    setVisible(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!value || submitting) return;

    setSubmitting(true);
    try {
      await apiService.post(
        "/api/streams/stream-rating/",
        { stream: streamId, rating: value, comment: comment.trim() },
        true,
      );
      localStorage.setItem(dismissedKey(streamId), "1");
      setSubmitted(true);
      setTimeout(() => {
        setVisible(false);
        onClose();
      }, THANK_YOU_DURATION_MS);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      showErrorToast("Couldn't submit your feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fade in={visible}>
      <Paper
        elevation={6}
        sx={{
          position: "fixed",
          bottom: { xs: 16, md: 24 },
          right: { xs: 16, md: 24 },
          left: { xs: 16, md: "auto" },
          width: { xs: "auto", md: 340 },
          borderRadius: "16px",
          p: 2.5,
          zIndex: 1300,
          pointerEvents: visible ? "auto" : "none",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {submitted && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              bottom: 0,
              height: 3,
              width: shrinkBar ? "0%" : "100%",
              bgcolor: "primary.main",
              transition: `width ${THANK_YOU_DURATION_MS}ms linear`,
            }}
          />
        )}

        {submitted ? (
          <Box sx={{ textAlign: "center", py: 1.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              Thank you!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your feedback helps us improve.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontWeight: 700, mb: 0.5, pr: 3 }}>
              How would you rate the livestream?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              You&apos;ve been with us a while, let us know how it&apos;s going
            </Typography>

            <Rating
              size="large"
              value={value}
              onChange={(_, newValue) => setValue(newValue)}
            />

            {value && (
              <Fade in>
                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    placeholder="Anything you'd like to add? (optional)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{ mt: 1.5, textTransform: "none", borderRadius: "10px" }}
                  >
                    {submitting ? "Submitting…" : "Submit feedback"}
                  </Button>
                </Box>
              </Fade>
            )}
          </>
        )}
      </Paper>
    </Fade>
  );
}